import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthenticatedResult } from 'angular-auth-oidc-client';
import { BehaviorSubject, Observable, combineLatest, map, shareReplay } from 'rxjs';
import { AuthStoreStrategy } from '../../feature-seatmap/strategies/authorization/auth-store.strategy';
import { OidcSecurityServiceStrategy } from '../../feature-seatmap/strategies/authorization/oidc-security-service.strategy';

@Injectable({ providedIn: 'root' })
export class AuthStoreService extends AuthStoreStrategy {
  private readonly oidcSecurityService = inject(OidcSecurityServiceStrategy);
  private readonly _bimplusUserId = signal('');

  private readonly authState = toSignal(this.oidcSecurityService.isAuthenticated$, {
    initialValue: { isAuthenticated: false, allConfigsAuthenticated: [] },
  });

  readonly isAuthenticated = computed(() => this.authState().isAuthenticated);

  /** Opaque user identifier from the OIDC `sub` claim — stable across sessions, contains no PII. */
  readonly userSub = toSignal(
    this.oidcSecurityService.userData$.pipe(map(({ userData }) => (userData?.sub as string) ?? '')),
    { initialValue: '' }
  );

  /** User identifier from Bimplus `/v2/user` endpoint. */
  readonly bimplusUserId = this._bimplusUserId.asReadonly();

  private readonly _isAuthenticatedOverride$ = new BehaviorSubject<AuthenticatedResult | null>(
    null
  );

  /**
   * Combined auth state: uses override if set, otherwise falls back to OIDC state.
   * Override takes precedence when not null.
   */
  readonly isAuthenticated$ = combineLatest([
    this.oidcSecurityService.isAuthenticated$,
    this._isAuthenticatedOverride$,
  ]).pipe(
    map(([oidcState, override]) => override ?? oidcState),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  setAuthenticated(value: AuthenticatedResult): void {
    this._isAuthenticatedOverride$.next(value);
  }

  clearAuthenticatedOverride(): void {
    this._isAuthenticatedOverride$.next(null);
  }

  /**
   * BehaviorSubject of the current access token. Holds the latest token value.
   */
  private readonly _accessToken$ = new BehaviorSubject<string>('');
  readonly accessToken$ = this._accessToken$.asObservable();

  updateAccessToken(token: string): void {
    this._accessToken$.next(token);
  }

  updateBimplusUserId(userId: string): void {
    this._bimplusUserId.set(userId);
  }

  /** Drops the local session: OIDC state, auth override, access token and cached Bimplus user id. */
  resetSession(): void {
    this.oidcSecurityService.logoffLocal();
    this.clearAuthenticatedOverride();
    this.updateAccessToken('');
    this.updateBimplusUserId('');
  }

  /** Get access token for API calls */
  getAccessToken(): Observable<string> {
    return this.oidcSecurityService.getAccessToken();
  }

  /** Get ID token containing user claims */
  getIdToken(): Observable<string> {
    return this.oidcSecurityService.getIdToken();
  }
}
